import { useProjectStore } from '../projectStore';
import { createPaneId, createTabId, LayoutState, TabContainer } from '../../../types';

// デフォルトレイアウト
const defaultLayout: LayoutState = {
  rootPane: {
    type: 'container',
    id: createPaneId('default-container'),
    tabs: [],
    activeTabId: createTabId('')
  } as TabContainer,
  activeContainerId: undefined
};

/**
 * PaneLayoutContextのusePaneLayoutと互換性のあるフック
 * projectStoreを使用してレイアウト操作を行う
 */
export const usePaneLayout = () => {
  const getCurrentWorkspace = useProjectStore(state => state.getCurrentWorkspace);

  // 現在のワークスペースのレイアウトを取得
  const workspace = getCurrentWorkspace();
  const layout = workspace?.layoutState ?? defaultLayout;

  // アクションを取得
  const addTab = useProjectStore(state => state.addTab);
  const closeTab = useProjectStore(state => state.closeTab);
  const activateTab = useProjectStore(state => state.activateTab);
  const updateTabState = useProjectStore(state => state.updateTabState);
  const moveTab = useProjectStore(state => state.moveTab);
  const moveTabWithinPane = useProjectStore(state => state.moveTabWithinPane);
  const splitPane = useProjectStore(state => state.splitPane);
  const closePane = useProjectStore(state => state.closePane);
  const updateSplitRatio = useProjectStore(state => state.updateSplitRatio);
  const findContainer = useProjectStore(state => state.findContainer);
  const findTab = useProjectStore(state => state.findTab);
  const getActiveContainer = useProjectStore(state => state.getActiveContainer);
  const handleDropZone = useProjectStore(state => state.handleDropZone);
  const setLayout = useProjectStore(state => state.setLayout);

  // 既存のPaneLayoutContextと互換性のあるインターフェースを返す
  return {
    layout,
    addTab,
    addTabWithOptions: addTab, // エイリアス
    updateLayout: (updater: (currentLayout: LayoutState) => LayoutState) => {
      setLayout(updater(layout));
    },
    closeTab,
    activateTab,
    updateTabState,
    moveTab,
    moveTabWithinPane,
    moveTabToPane: moveTab, // エイリアス
    startDrag: (_tabId: string, _containerId: string) => {
      // ドラッグ状態は別の場所で管理される
    },
    endDrag: () => {
      // ドラッグ状態は別の場所で管理される
    },
    splitPane,
    closePane,
    updateSplitRatio,
    findContainer,
    findTab,
    getActiveContainer,
    handleDropZone
  };
};

/**
 * 特定のペインのタブ情報を取得するフック
 */
export const usePaneTabs = (paneId: string) => {
  const findContainer = useProjectStore(state => state.findContainer);
  const container = findContainer(paneId);

  return {
    tabs: container?.tabs || [],
    activeTabId: container?.activeTabId || ''
  };
};

/**
 * 現在アクティブなペインとタブの情報を取得するフック
 */
export const useActivePane = () => {
  const getCurrentWorkspace = useProjectStore(state => state.getCurrentWorkspace);
  const getActiveContainer = useProjectStore(state => state.getActiveContainer);

  const workspace = getCurrentWorkspace();
  const activeContainerId = workspace?.layoutState.activeContainerId;
  const activeContainer = getActiveContainer();

  return {
    activeContainerId,
    activeContainer,
    activeTabId: activeContainer?.activeTabId || ''
  };
};
